import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { pcmpMoReportQueryOptions } from '../api/pcmpMoReport.api';
import { PcmpPrintSheet } from '../components/PcmpPrint/PcmpPrintSheet';
import { PcmpMoReportSheet } from '../components/PcmpReports/PcmpMoReportSheet';
import type { PcmpMoReportFilters } from '../types/pcmpMoReport.types';

const route = getRouteApi('/_print/pcmp/reports/mo-wise/print');

/** The MO Wise report on landscape A4 sheets, opening the print dialog once loaded. */
export function PcmpMoReportPrintPage() {
  const search = route.useSearch();
  const { scope, moUserId, reportingUserId, departmentId, division, projectIds, dateFrom, dateTo } =
    search;

  const filters = useMemo<PcmpMoReportFilters>(
    () => ({
      scope: scope ?? 'MO',
      moUserId,
      reportingUserId,
      departmentId,
      division,
      projectIds,
      dateFrom,
      dateTo,
    }),
    [scope, moUserId, reportingUserId, departmentId, division, projectIds, dateFrom, dateTo],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    ...pcmpMoReportQueryOptions(filters),
    // Closing the print dialog refocuses the tab; keep the sheet as printed.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) document.title = `MO Wise PCMP Report ${data.reportDate}`;
  }, [data]);

  if (isLoading) return <PageLoader />;

  if (error || !data) {
    return (
      <div className="p-6">
        <ErrorState
          error={error}
          fallbackTitle="Could not load the MO Wise report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  return (
    <PcmpPrintSheet>
      <PcmpMoReportSheet report={data} />
    </PcmpPrintSheet>
  );
}
